import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  materialiseCriteria,
  evaluateDataOverlay,
  applySlotOptions
} from './evaluate';
import { criteriaSentence, familyOptions } from './vocabulary';

// Refining a data overlay (TGCLOUD-1670).
//
// The grammar is CascadeScreeningButton's, deliberately: a clinician who has
// used cascade screening already knows this popover. Parameter controls, live
// counts beside every choice, one aria-live summary line, Apply and Cancel.
//
// Live counts are the point. "Overdue by 7 days" versus "overdue by 21 days" is
// not a question anyone can answer in the abstract; it is answerable the moment
// the popover says one picks four people and the other picks one. Every count is
// evaluated against the SAME payload the chart will use, so the number cannot
// disagree with what appears when Apply is pressed.

const SUMMARY_SX = {
  fontSize: 12,
  color: 'text.secondary',
  mt: 1.25,
  minHeight: 32
};

/** Preview a candidate parameter set without applying it. */
const countFor = (preset, params, evalArgs) => {
  try {
    const results = evaluateDataOverlay({
      preset,
      criteria: materialiseCriteria(preset, params),
      ...evalArgs
    });
    return applySlotOptions(results, evalArgs.slotOptions).count;
  } catch (_) {
    return null;
  }
};

const IntParam = ({ def, value, onChange, preset, params, evalArgs }) => {
  const bands = def.bands || [];
  const inBands = bands.includes(Number(value));

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {def.label}
      </Typography>
      {bands.length > 0 && (
        <RadioGroup
          value={inBands ? String(value) : ''}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {bands.map((band) => {
            const n = countFor(
              preset,
              { ...params, [def.key]: band },
              evalArgs
            );
            return (
              <FormControlLabel
                key={band}
                value={String(band)}
                control={<Radio size="small" />}
                sx={{ my: -0.5 }}
                label={
                  <Typography sx={{ fontSize: 13 }}>
                    {band} days{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      ({n == null ? '—' : n})
                    </Box>
                  </Typography>
                }
              />
            );
          })}
        </RadioGroup>
      )}
      <TextField
        size="small"
        type="number"
        value={value ?? ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : Number(e.target.value))
        }
        slotProps={{
          // MUI 9: `inputProps` is deprecated and, more to the point here, no
          // longer reaches the native input, so the aria-label silently went
          // nowhere and the control had no accessible name.
          htmlInput: { min: def.min, max: def.max, 'aria-label': def.label }
        }}
        sx={{ mt: bands.length ? 0.5 : 0, width: 120 }}
      />
    </Box>
  );
};

const SelectParam = ({ def, value, onChange, options, multiple }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
      {def.label}
    </Typography>
    <Select
      size="small"
      fullWidth
      multiple={multiple}
      value={multiple ? value || [] : value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      displayEmpty
      renderValue={(v) => {
        const list = multiple ? v : [v];
        if (!list || !list.length || list[0] === '' || list[0] == null) {
          return (
            <Typography
              component="span"
              sx={{ fontSize: 13, color: 'text.disabled' }}
            >
              Choose
            </Typography>
          );
        }
        return list
          .map((x) => options.find((o) => o.value === x)?.label || String(x))
          .join(', ');
      }}
      inputProps={{ 'aria-label': def.label }}
    >
      {options.length === 0 && (
        <MenuItem value="" disabled>
          Nothing recorded in this family
        </MenuItem>
      )}
      {options.map((o) => (
        <MenuItem key={String(o.value)} value={o.value} sx={{ fontSize: 13 }}>
          {o.label}
          {o.observed && (
            <Chip
              size="small"
              label="observed"
              sx={{ ml: 1, height: 18, fontSize: 10 }}
            />
          )}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

const DataOverlayCriteriaPopover = ({
  open,
  anchorEl,
  onClose,
  preset,
  params,
  slotOptions,
  onApply,
  facts,
  personIndex,
  asOf,
  fmtDate,
  memberRecords,
  diagnoses,
  accent
}) => {
  const [draft, setDraft] = React.useState(params || {});
  const [draftOptions, setDraftOptions] = React.useState(
    slotOptions || { countHidden: true, markEveryRendering: true }
  );

  // Reopening after a change elsewhere must show the live values, not whatever
  // was last typed and cancelled.
  React.useEffect(() => {
    if (!open) return;
    setDraft(params || {});
    setDraftOptions(
      slotOptions || { countHidden: true, markEveryRendering: true }
    );
  }, [open, params, slotOptions]);

  const evalArgs = React.useMemo(
    () => ({ facts, personIndex, asOf, fmtDate, slotOptions: draftOptions }),
    [facts, personIndex, asOf, fmtDate, draftOptions]
  );

  const preview = React.useMemo(() => {
    if (!preset) return null;
    try {
      const criteria = materialiseCriteria(preset, draft);
      const results = evaluateDataOverlay({
        preset,
        criteria,
        facts,
        personIndex,
        asOf,
        fmtDate
      });
      return { criteria, results: applySlotOptions(results, draftOptions) };
    } catch (_) {
      return null;
    }
  }, [preset, draft, facts, personIndex, asOf, fmtDate, draftOptions]);

  if (!preset) return null;

  const setParam = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const r = preview?.results;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {preset.title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
          {preset.description}
        </Typography>

        {preview?.criteria && (
          <Typography
            sx={{
              fontSize: 11.5,
              mt: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              color: 'text.secondary'
            }}
          >
            {criteriaSentence(preview.criteria)}
          </Typography>
        )}

        {(preset.paramDefs || []).map((def) => {
          const value = draft[def.key] ?? def.default;
          if (def.type === 'int') {
            return (
              <IntParam
                key={def.key}
                def={def}
                value={value}
                onChange={(v) => setParam(def.key, v)}
                preset={preset}
                params={draft}
                evalArgs={evalArgs}
              />
            );
          }
          const options = def.options
            ? def.options
            : familyOptions(def.optionsSource, { memberRecords, diagnoses });
          return (
            <SelectParam
              key={def.key}
              def={def}
              value={value}
              onChange={(v) => setParam(def.key, v)}
              options={options}
              multiple={def.type === 'multiEnum'}
            />
          );
        })}

        <Divider sx={{ my: 1.5 }} />

        <FormControlLabel
          sx={{ display: 'flex', my: -0.5 }}
          control={
            <Checkbox
              size="small"
              checked={draftOptions.countHidden !== false}
              onChange={(e) =>
                setDraftOptions((o) => ({
                  ...o,
                  countHidden: e.target.checked
                }))
              }
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>Count hidden members</Typography>
          }
        />
        <FormControlLabel
          sx={{ display: 'flex', my: -0.5 }}
          control={
            <Checkbox
              size="small"
              checked={draftOptions.markEveryRendering !== false}
              onChange={(e) =>
                setDraftOptions((o) => ({
                  ...o,
                  markEveryRendering: e.target.checked
                }))
              }
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>
              Mark every rendering of a person
            </Typography>
          }
        />

        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
          Hidden members are counted but not drawn.
        </Typography>

        <Typography sx={SUMMARY_SX} aria-live="polite">
          {r
            ? [
                `${r.count} of ${r.rosterSize} family members match`,
                r.hiddenCount ? `${r.hiddenCount} hidden` : null,
                r.unresolvedCount ? `${r.unresolvedCount} unresolved` : null,
                `as of ${fmtDate ? fmtDate(r.asOf) : r.asOf}`
              ]
                .filter(Boolean)
                .join(' · ')
            : 'Records could not be read'}
        </Typography>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}
        >
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ backgroundColor: accent }}
            onClick={() => {
              onApply(draft, draftOptions);
              onClose();
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default DataOverlayCriteriaPopover;
